import { showBladeToast } from "@/shared/toast";
import { openModal as openSharedModal } from "@/shared/modal";
import { submitSavedSearch } from "@/shared/saved-search-form";

type MarketplaceSaveSearchModalOptions = {
    marketplaceEquivalentUrl: string | null;
};

function csrfToken(): string {
    return (
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
            ?.content ?? ""
    );
}

function resolveSearchUrlToSave(
    marketplaceEquivalentUrl: string | null,
): string {
    if (marketplaceEquivalentUrl) {
        return `${window.location.origin}${marketplaceEquivalentUrl}`;
    }

    return decodeURIComponent(window.location.href);
}

function suggestTitleFromUrl(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const make = urlParams.get("menu[make]");
    const model = urlParams.getAll("refinementList[model][0]")[0];

    if (!make) {
        return "";
    }

    return model ? `${make} ${model}` : make;
}

function hasActiveSearchFilters(
    marketplaceEquivalentUrl: string | null,
): boolean {
    // Curated page: SavedSearch.vue only looks at the URL query (any parameter), so a
    // curated page without one shows « Aucun filtre actif » (reproduced as is).
    if (window.__MARKETPLACE_CONFIG__?.curatedLayout) {
        return [...new URLSearchParams(window.location.search).keys()].length > 0;
    }

    if (marketplaceEquivalentUrl) {
        return true;
    }

    const params = new URLSearchParams(window.location.search);
    return [...params.keys()].some((key) => key !== "page");
}

function setPanelVisible(
    modalCard: HTMLElement,
    panels: Record<string, HTMLElement | null>,
    activeKey: string,
): void {
    modalCard.dataset.panel = activeKey;

    for (const [key, panel] of Object.entries(panels)) {
        if (!panel) {
            continue;
        }

        panel.hidden = key !== activeKey;
    }
}

function closeModal(modal: HTMLElement): void {
    modal.hidden = true;
    document.body.style.overflow = "";
}

function openModal(modal: HTMLElement): void {
    modal.hidden = false;
    document.body.style.overflow = "hidden";
}

/**
 * Curated SEO search: SavedSearch.vue's Modal (shared partials/modal templates). Same
 * states as the Vue component: « Aucun filtre actif » without URL query, the title form
 * (pre-filled from menu[make] / refinementList[model][0]), then the saved confirmation;
 * the server validation message shows under the input (Inertia form errors).
 */
function initVueSaveSearchModal(marketplaceEquivalentUrl: string | null): boolean {
    const template = (name: string) =>
        document.querySelector<HTMLTemplateElement>(`template[data-modal-template="${name}"]`);
    const none = template("saved-search-none");
    const formTemplate = template("saved-search-form");
    const saved = template("saved-search-saved");
    if (!none || !formTemplate || !saved) return false;

    document.body.addEventListener(
        "click",
        (event) => {
            const btn = (event.target as Element | null)?.closest<HTMLButtonElement>(
                'button[data-testid="save-search-btn"]',
            );
            if (!btn || btn.hasAttribute("data-open-login")) return;
            event.preventDefault();

            if (!hasActiveSearchFilters(marketplaceEquivalentUrl)) {
                openSharedModal(none);
                return;
            }

            const modal = openSharedModal(formTemplate);
            const form = modal.querySelector<HTMLFormElement>("[data-saved-search-form]");
            const input = form?.querySelector<HTMLInputElement>("input");
            if (input) input.value = suggestTitleFromUrl();
            form?.addEventListener("submit", (submitEvent) => {
                submitEvent.preventDefault();
                submitSavedSearch(modal, form, resolveSearchUrlToSave(marketplaceEquivalentUrl), saved);
            });
        },
        { capture: true },
    );

    return true;
}

export function initMarketplaceSaveSearchModal({
    marketplaceEquivalentUrl,
}: MarketplaceSaveSearchModalOptions): void {
    if (window.__MARKETPLACE_CONFIG__?.curatedLayout && initVueSaveSearchModal(marketplaceEquivalentUrl)) {
        return;
    }

    const modal = document.getElementById("marketplace-save-search-modal");
    const form = document.getElementById(
        "marketplace-save-search-form",
    ) as HTMLFormElement | null;
    const titleInput = document.getElementById(
        "marketplace-save-search-title",
    ) as HTMLInputElement | null;
    const errorEl = document.getElementById("marketplace-save-search-error");

    const panels = {
        form: document.getElementById("marketplace-save-search-form-panel"),
        noFilters: document.getElementById("marketplace-save-search-no-filters"),
        success: document.getElementById("marketplace-save-search-success"),
    };

    if (!modal || !form || !titleInput) {
        return;
    }

    const modalCard = modal.querySelector<HTMLElement>(".save-search-modal__card");
    if (!modalCard) {
        return;
    }

    const resetModalState = (): void => {
        setPanelVisible(modalCard, panels, "form");
        if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = "";
        }
        titleInput.value = "";
    };

    modal.querySelectorAll("[data-close-modal]").forEach((el) => {
        el.addEventListener("click", () => {
            closeModal(modal);
            resetModalState();
        });
    });

    document.body.addEventListener(
        "click",
        (event) => {
            const btn = (event.target as Element | null)?.closest<HTMLButtonElement>(
                'button[data-testid="save-search-btn"]',
            );
            if (!btn || btn.hasAttribute("data-open-login")) {
                return;
            }

            event.preventDefault();

            if (!hasActiveSearchFilters(marketplaceEquivalentUrl)) {
                setPanelVisible(modalCard, panels, "noFilters");
                openModal(modal);
                return;
            }

            resetModalState();
            titleInput.value = suggestTitleFromUrl();
            openModal(modal);
            titleInput.focus();
        },
        { capture: true },
    );

    form.addEventListener("submit", (event) => {
        event.preventDefault();

        const title = titleInput.value.trim();
        if (!title) {
            if (errorEl) {
                errorEl.textContent = "Le titre de la recherche est obligatoire.";
                errorEl.hidden = false;
            }
            return;
        }

        void (async () => {
            const searchUrl = resolveSearchUrlToSave(marketplaceEquivalentUrl);

            try {
                const response = await fetch("/savedSearchs", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": csrfToken(),
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        search_url: searchUrl,
                        title,
                    }),
                });

                if (!response.ok) {
                    const payload = (await response.json().catch(() => null)) as {
                        message?: string;
                        errors?: Record<string, string[]>;
                    } | null;
                    const validationMessage =
                        payload?.errors?.search_url?.[0] ??
                        payload?.errors?.title?.[0];
                    const message =
                        validationMessage ??
                        payload?.message ??
                        "Impossible de sauvegarder la recherche.";

                    if (errorEl) {
                        errorEl.textContent = message;
                        errorEl.hidden = false;
                    } else {
                        showBladeToast(message, "error");
                    }
                    return;
                }

                setPanelVisible(modalCard, panels, "success");
            } catch {
                showBladeToast(
                    "Impossible de sauvegarder la recherche.",
                    "error",
                );
            }
        })();
    });
}
